/**
 * Arrange's first question: the score.
 *
 * Before there is one it is a place to drop a file, which is also a button that opens the file
 * picker, with the Library one link away. Once there is one it is the score's name and how
 * much music is in it, which is how the reader sees that the right file went in, and a way to
 * change it.
 */

import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SCORE_ACCEPT } from "../lib/arrange-open";
import { plural } from "../lib/arrange-words";
import ArrangeStep from "./ArrangeStep";

export interface ArrangeStepScoreProps {
  /** The score's name, or `null` before one is opened. */
  readonly name: string | null;
  /** How much music is in it, or `null` where this browser could not read it. */
  readonly facts: { readonly bars: number; readonly parts: readonly string[] } | null;
  /** The library score being fetched, by id, or `null` where none is. */
  readonly fetching: string | null;
  /** How many photos of the score's pages are being read, or `null` where none are. */
  readonly photos: number | null;
  /** Why the library could not hand a score over, or `null`. */
  readonly trouble: string | null;
  /** A score picked: one file, or several photos or pictures of its pages. */
  onOpen(files: readonly File[]): void;
}

export default function ArrangeStepScore({
  name,
  facts,
  fetching,
  photos,
  trouble,
  onOpen,
}: ArrangeStepScoreProps): JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const choose = (): void => input.current?.click();

  return (
    <ArrangeStep number={1} title="Score">
      <input
        ref={input}
        type="file"
        accept={SCORE_ACCEPT}
        multiple
        className="hidden"
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          // Emptied so that picking the same file again is still a change.
          event.target.value = "";
          if (files.length > 0) onOpen(files);
        }}
      />
      {fetching !== null ? (
        <p className="rounded-lg border border-shell-seam bg-shell px-3.5 py-3 text-[14px] text-shell-text">
          Fetching {fetching} from the Library…
        </p>
      ) : name === null ? (
        <>
          <button
            type="button"
            onClick={choose}
            onDragOver={(event) => {
              event.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setOver(false);
              const files = [...event.dataTransfer.files];
              if (files.length > 0) onOpen(files);
            }}
            className={`flex min-h-[112px] flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed bg-shell p-4 text-center text-shell-lit hover:border-shell-lit ${
              over ? "border-shell-live" : "border-shell-dim"
            }`}
          >
            <span className="text-[15px] font-medium">Drop a score here, or choose a file</span>
            <span className="text-[12px] text-shell-text">
              MusicXML, MIDI, or a PDF or photos of printed music
            </span>
            <span className="text-[12px] text-shell-dim">
              Several photos are read as one score, in the order of their names
            </span>
          </button>
          <Link
            to="/library"
            className="self-start py-1 text-[13px] text-shell-text underline decoration-shell-dim underline-offset-2 hover:text-shell-lit"
          >
            or take one from the Library
          </Link>
        </>
      ) : (
        <div className="flex items-center gap-3 rounded-lg border border-shell-seam bg-shell py-1 pl-3.5 pr-1">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-2">
            <span className="truncate text-[15px] text-shell-lit">{name}</span>
            <span className="font-mono text-[12px] text-shell-text">
              {photos !== null
                ? `${plural(photos, "photo")} · being read`
                : facts === null
                  ? "This browser could not read it; the arranger may."
                  : `${plural(facts.bars, "bar")} · ${plural(facts.parts.length, "part")}`}
            </span>
          </div>
          <button
            type="button"
            onClick={choose}
            className="h-11 shrink-0 rounded-md px-3 text-[13px] text-shell-text hover:bg-shell-hot hover:text-shell-lit"
          >
            Change
          </button>
        </div>
      )}
      {trouble === null ? null : (
        <p role="alert" className="rounded-md bg-fault/20 px-3 py-2 text-[13px] text-shell-lit">
          {trouble}
        </p>
      )}
    </ArrangeStep>
  );
}
